import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Fab,
  Alert,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Switch,
  FormControlLabel,
  Tabs,
  Tab as MuiTab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Tab as TabIcon,
  Visibility as ViewIcon,
  VisibilityOff as EditModeIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Import real dashboard components
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createMuiTheme } from '../utils/themeUtils';
import { DragDropProvider } from '../contexts/DragDropContext';
import { DashboardContext } from '../contexts/DashboardContext';
import TabEditDialog from './TabEditDialog';
import GroupEditDialog from './Group/GroupEditDialog';
import TileEditDialog from './Tile/TileEditDialog';
import TileCard from './Tile/TileCard';
import IconDisplay from './IconDisplay/IconDisplay';

import { DashboardConfig, DashboardTheme, Group, Tile, Tab as TabType, TabFormData, GroupFormData, TileFormData, DialogMode, SearchResult, CreateTabRequest, UpdateTabRequest, CreateGroupRequest, UpdateGroupRequest, CreateTileRequest, UpdateTileRequest } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi } from '../utils/api';
import ThinkBeyondLogo from '../assets/images/thumbnail_Think Beyond AI-favicon.jpg';

interface DashboardPreviewProps {
  config: DashboardConfig;
  onConfigChange?: (config: DashboardConfig) => void;
  isEditable?: boolean;
}

// Internal preview context to manage state independently
interface PreviewContextValue {
  config: DashboardConfig | null;
  editMode: boolean;
  activeTab: TabType | null;
  selectedItem: { type: 'tile' | 'group' | 'tab'; id: string } | null;
  isDirty: boolean;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  searchResults: any[];
  updateConfig: (newConfig: DashboardConfig) => Promise<void>;
  toggleEditMode: () => void;
  setActiveTab: (tabId: string) => Promise<void>;
  setSelectedItem: (item: { type: 'tile' | 'group' | 'tab'; id: string } | null) => void;
  saveConfig: () => Promise<void>;
  resetConfig: () => Promise<void>;
  updateTheme: (theme: any) => Promise<void>;
  createTab: (tabData: any) => Promise<void>;
  updateTab: (tabId: string, tabData: any) => Promise<void>;
  deleteTab: (tabId: string) => Promise<void>;
  reorderTabs: (sourceIndex: number, destIndex: number) => void;
  // Tile operations needed by TileCard
  updateTile: (id: string, updates: any) => Promise<void>;
  deleteTile: (id: string) => Promise<void>;
  // Additional properties to match DashboardContextType
  loadConfig: () => Promise<void>;
  createGroup: (group: CreateGroupRequest) => Promise<void>;
  updateGroup: (id: string, updates: UpdateGroupRequest) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  reorderGroups: (sourceIndex: number, destIndex: number) => void;
  createTile: (groupId: string, tile: CreateTileRequest) => Promise<void>;
  moveTile: (tileId: string, targetGroupId: string, targetIndex?: number) => void;
  reorderTilesInGroup: (groupId: string, sourceIndex: number, destIndex: number) => void;
  setSearchQuery: (query: string) => void;
  performSearch: (query: string) => Promise<void>;
  clearSearch: () => void;
}

const PreviewContext = React.createContext<PreviewContextValue | null>(null);

const usePreviewContext = () => {
  const context = React.useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreviewContext must be used within PreviewProvider');
  }
  return context;
};

// Hook that mimics useDashboard for child components - REMOVED
// We'll use a different approach to provide dashboard context compatibility

// Preview provider component
const PreviewProvider: React.FC<{ 
  children: React.ReactNode; 
  config: DashboardConfig;
  onConfigChange?: (config: DashboardConfig) => void;
}> = ({ children, config: initialConfig, onConfigChange }) => {
  const [config, setConfig] = useState<DashboardConfig>(initialConfig);
  const [editMode, setEditMode] = useState(true); // Always in edit mode for preview
  const [selectedItem, setSelectedItem] = useState<{ type: 'tile' | 'group' | 'tab'; id: string } | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Sync with parent config changes
  useEffect(() => {
    setConfig(initialConfig);
  }, [initialConfig]);

  const activeTab = useMemo(() => {
    if (!config.tabs || config.tabs.length === 0) return null;
    return config.tabs.find(tab => tab.id === config.activeTabId) || config.tabs[0];
  }, [config]);

  const updateConfig = async (newConfig: DashboardConfig) => {
    setConfig(newConfig);
    setIsDirty(true);
    onConfigChange?.(newConfig);
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const setActiveTabHandler = async (tabId: string) => {
    const newConfig = {
      ...config,
      activeTabId: tabId
    };
    await updateConfig(newConfig);
  };

  // Mock functions for dashboard compatibility
  const saveConfig = async () => {
    // In preview mode, just mark as not dirty
    setIsDirty(false);
  };

  const resetConfig = async () => {
    // Reset to initial config
    setConfig(initialConfig);
    setIsDirty(false);
  };

  const updateTheme = async (theme: any) => {
    const newConfig = {
      ...config,
      theme: { ...config.theme, ...theme }
    };
    updateConfig(newConfig);
  };

  const createTab = async (tabData: any) => {
    // Handled by handleTabSubmit in component
  };

  const updateTabFunc = async (tabId: string, tabData: any) => {
    // Handled by handleTabSubmit in component  
  };

  const deleteTabFunc = async (tabId: string) => {
    // Handled by handleTabDelete in component
  };

  const reorderTabs = (sourceIndex: number, destIndex: number) => {
    // Handled by handleTabReorder in component
  };

  // Tile operations for TileCard compatibility
  const updateTile = async (tileId: string, updates: any) => {
    if (!config || !config.tabs) return;
    
    const newTabs = config.tabs.map(tab => ({
      ...tab,
      groups: tab.groups?.map(group => ({
        ...group,
        tiles: group.tiles?.map(tile => 
          tile.id === tileId ? { ...tile, ...updates } : tile
        ) || []
      })) || []
    }));
    
    const newConfig: DashboardConfig = {
      ...config,
      tabs: newTabs
    };
    updateConfig(newConfig);
  };

  const deleteTile = async (tileId: string) => {
    if (!config || !config.tabs) return;
    
    const newTabs = config.tabs.map(tab => ({
      ...tab,
      groups: tab.groups?.map(group => ({
        ...group,
        tiles: group.tiles?.filter(tile => tile.id !== tileId) || []
      })) || []
    }));
    
    const newConfig: DashboardConfig = {
      ...config,
      tabs: newTabs
    };
    updateConfig(newConfig);
  };

  // Additional functions to match DashboardContextType
  const loadConfig = async () => {
    // No-op in preview mode
  };

  const createGroup = async (group: CreateGroupRequest) => {
    // Handled by component-level functions
  };

  const updateGroup = async (id: string, updates: UpdateGroupRequest) => {
    // Handled by component-level functions
  };

  const deleteGroup = async (id: string) => {
    // Handled by component-level functions
  };

  const reorderGroups = (sourceIndex: number, destIndex: number) => {
    // Handled by component-level functions
  };

  const createTile = async (groupId: string, tile: CreateTileRequest) => {
    // Handled by component-level functions
  };

  const moveTile = (tileId: string, targetGroupId: string, targetIndex?: number) => {
    if (!config || !config.tabs) return;
    
    // Find and remove tile from source group across all tabs
    let movedTile: Tile | null = null;
    const newTabs = config.tabs.map(tab => ({
      ...tab,
      groups: tab.groups?.map(group => {
        const tileIndex = group.tiles?.findIndex(tile => tile.id === tileId) || -1;
        if (tileIndex !== -1) {
          movedTile = group.tiles![tileIndex];
          return {
            ...group,
            tiles: group.tiles?.filter(tile => tile.id !== tileId) || []
          };
        }
        return group;
      }) || []
    }));
    
    if (!movedTile) return;
    
    // Add tile to target group
    const finalTabs = newTabs.map(tab => ({
      ...tab,
      groups: tab.groups?.map(group => {
        if (group.id === targetGroupId) {
          const tiles = [...(group.tiles || [])];
          const insertIndex = targetIndex ?? tiles.length;
          tiles.splice(insertIndex, 0, { ...movedTile!, order: insertIndex });
          
          // Update order values for all tiles in the group
          return {
            ...group,
            tiles: tiles.map((tile, index) => ({ ...tile, order: index }))
          };
        }
        return group;
      }) || []
    }));
    
    const newConfig: DashboardConfig = {
      ...config,
      tabs: finalTabs
    };
    updateConfig(newConfig);
  };

  const reorderTilesInGroup = (groupId: string, sourceIndex: number, destIndex: number) => {
    if (!config || !config.tabs) return;
    
    const newTabs = config.tabs.map(tab => ({
      ...tab,
      groups: tab.groups?.map(group => {
        if (group.id === groupId) {
          const tiles = [...(group.tiles || [])];
          const [movedTile] = tiles.splice(sourceIndex, 1);
          tiles.splice(destIndex, 0, movedTile);
          
          // Update order values for all tiles in the group
          return {
            ...group,
            tiles: tiles.map((tile, index) => ({ ...tile, order: index }))
          };
        }
        return group;
      }) || []
    }));
    
    const newConfig: DashboardConfig = {
      ...config,
      tabs: newTabs
    };
    updateConfig(newConfig);
  };

  const setSearchQuery = (query: string) => {
    // No-op in preview mode
  };

  const performSearch = async (query: string) => {
    // No-op in preview mode
  };

  const clearSearch = () => {
    // No-op in preview mode
  };

  const value: PreviewContextValue = {
    config,
    editMode,
    activeTab,
    selectedItem,
    isDirty,
    isLoading: false,
    error: null,
    searchQuery: '',
    searchResults: [],
    updateConfig,
    toggleEditMode,
    setActiveTab: setActiveTabHandler,
    setSelectedItem,
    saveConfig,
    resetConfig,
    updateTheme,
    createTab,
    updateTab: updateTabFunc,
    deleteTab: deleteTabFunc,
    reorderTabs,
    updateTile,
    deleteTile,
    loadConfig,
    createGroup,
    updateGroup,
    deleteGroup,
    reorderGroups,
    createTile,
    moveTile,
    reorderTilesInGroup,
    setSearchQuery,
    performSearch,
    clearSearch,
  };

  return (
    <PreviewContext.Provider value={value}>
      <DashboardContext.Provider value={value}>
        {children}
      </DashboardContext.Provider>
    </PreviewContext.Provider>
  );
};

// Main preview component
const DashboardPreviewContent: React.FC = () => {
  // Try to get user info for admin features, but handle gracefully if not available
  let user: any = null;
  try {
    const authContext = useAuth();
    user = authContext?.user;
  } catch (e) {
    // Auth context not available in some contexts
  }
  const {
    config,
    editMode,
    activeTab,
    toggleEditMode,
    setActiveTab,
    updateConfig,
    isDirty
  } = usePreviewContext();

  const [tabDialogOpen, setTabDialogOpen] = useState(false);
  const [tabDialogMode, setTabDialogMode] = useState<'create' | 'edit'>('create');
  const [editingTab, setEditingTab] = useState<TabType | null>(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Group dialog state
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupDialogMode, setGroupDialogMode] = useState<'create' | 'edit'>('create');
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);

  // Tile dialog state
  const [tileDialogOpen, setTileDialogOpen] = useState(false);
  const [tileDialogMode, setTileDialogMode] = useState<'create' | 'edit'>('create');
  const [editingTile, setEditingTile] = useState<Tile | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  // Apply to dashboard state
  const [isApplying, setIsApplying] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  // Title editing state removed - using static display only

  // Logo editing state removed - using global logo settings only

  // Initialize tabs if none exist
  useEffect(() => {
    if (!config || (!config.tabs || config.tabs.length === 0)) {
      if (!config) return; // Skip if config is null
      
      const defaultTab: TabType = {
        id: 'default-tab',
        name: 'Home',
        icon: 'home',
        color: config.theme?.primaryColor || '#1976d2',
        order: 1,
        groups: config.groups || []
      };
      
      const newConfig: DashboardConfig = {
        ...config,
        tabs: [defaultTab],
        activeTabId: defaultTab.id,
        groups: []
      };
      updateConfig(newConfig);
    }
  }, [config, updateConfig]);

  // Tab management functions
  const handleTabCreate = () => {
    setTabDialogMode('create');
    setEditingTab(null);
    setTabDialogOpen(true);
  };

  const handleTabEdit = (tab: TabType) => {
    setTabDialogMode('edit');
    setEditingTab(tab);
    setTabDialogOpen(true);
  };

  const handleTabDelete = (tabId: string) => {
    if (!config || !config.tabs || config.tabs.length <= 1) {
      alert('Cannot delete the last tab');
      return;
    }

    if (window.confirm('Delete this tab and all its groups/tiles?')) {
      const newTabs = config.tabs.filter(t => t.id !== tabId);
      let newActiveTabId = config.activeTabId;
      
      if (config.activeTabId === tabId) {
        newActiveTabId = newTabs[0]?.id || '';
      }

      const newConfig: DashboardConfig = {
        ...config,
        tabs: newTabs,
        activeTabId: newActiveTabId
      };
      updateConfig(newConfig);
    }
  };

  const handleTabReorder = (sourceIndex: number, destIndex: number) => {
    if (!config || !config.tabs) return;
    
    const newTabs = [...config.tabs];
    const [removed] = newTabs.splice(sourceIndex, 1);
    newTabs.splice(destIndex, 0, removed);
    
    const newConfig: DashboardConfig = {
      ...config,
      tabs: newTabs
    };
    updateConfig(newConfig);
  };

  const handleTabSubmit = async (tabData: TabFormData) => {
    if (!config) return;
    
    if (tabDialogMode === 'create') {
      // Create new tab
      const newTab: TabType = {
        id: `tab-${Date.now()}`,
        name: tabData.name,
        icon: tabData.icon,
        color: tabData.color,
        order: (config.tabs?.length || 0) + 1,
        groups: []
      };
      
      const newTabs = [...(config.tabs || []), newTab];
      const newConfig: DashboardConfig = {
        ...config,
        tabs: newTabs,
        activeTabId: newTab.id
      };
      updateConfig(newConfig);
      setActiveTab(newTab.id);
    } else if (editingTab) {
      // Edit existing tab
      const updatedTab: TabType = {
        ...editingTab,
        name: tabData.name,
        icon: tabData.icon,
        color: tabData.color
      };
      
      const newTabs = config.tabs?.map(tab => 
        tab.id === updatedTab.id ? updatedTab : tab
      ) || [];
      
      const newConfig: DashboardConfig = {
        ...config,
        tabs: newTabs
      };
      updateConfig(newConfig);
    }
    
    setTabDialogOpen(false);
  };

  const handleSave = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  // Apply preview configuration to actual dashboard
  const handleApplyToDashboard = async () => {
    if (!config || !user) return;

    setIsApplying(true);
    setApplyError(null);

    try {
      await dashboardApi.updateConfig(config);
      setApplySuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setApplySuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error('Failed to apply configuration to dashboard:', error);
      setApplyError(error.message || 'Failed to apply configuration to dashboard');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setApplyError(null);
      }, 5000);
    } finally {
      setIsApplying(false);
    }
  };

  // Group management functions
  const handleGroupEdit = (group: Group) => {
    setGroupDialogMode('edit');
    setEditingGroup(group);
    setGroupDialogOpen(true);
  };

  const handleGroupCreate = () => {
    setGroupDialogMode('create');
    setEditingGroup(null);
    setGroupDialogOpen(true);
  };

  const handleGroupSubmit = async (groupData: GroupFormData) => {
    if (!config || !activeTab) return;

    if (groupDialogMode === 'create') {
      // Create new group
      const newGroup: Group = {
        id: `group-${Date.now()}`,
        name: groupData.name,
        icon: groupData.icon,
        color: groupData.color,
        order: activeTab.groups.length + 1,
        isCollapsible: groupData.isCollapsible || true,
        isCollapsed: groupData.isCollapsed || false,
        tiles: []
      };

      const newTabs = config.tabs?.map(tab => 
        tab.id === activeTab.id 
          ? { ...tab, groups: [...tab.groups, newGroup] }
          : tab
      ) || [];

      const newConfig: DashboardConfig = {
        ...config,
        tabs: newTabs
      };
      await updateConfig(newConfig);
    } else if (editingGroup) {
      // Edit existing group
      const updatedGroup: Group = {
        ...editingGroup,
        name: groupData.name,
        icon: groupData.icon,
        color: groupData.color,
        isCollapsible: groupData.isCollapsible || true,
        isCollapsed: groupData.isCollapsed || false
      };

      const newTabs = config.tabs?.map(tab => 
        tab.id === activeTab.id 
          ? { 
              ...tab, 
              groups: tab.groups.map(group => 
                group.id === updatedGroup.id ? updatedGroup : group
              )
            }
          : tab
      ) || [];

      const newConfig: DashboardConfig = {
        ...config,
        tabs: newTabs
      };
      await updateConfig(newConfig);
    }

    setGroupDialogOpen(false);
  };

  // Tile management functions
  const handleTileEdit = (tile: Tile, groupId: string) => {
    setTileDialogMode('edit');
    setEditingTile(tile);
    setEditingGroupId(groupId);
    setTileDialogOpen(true);
  };

  const handleTileCreate = (groupId: string) => {
    setTileDialogMode('create');
    setEditingTile(null);
    setEditingGroupId(groupId);
    setTileDialogOpen(true);
  };

  const handleTileSubmit = async (tileData: TileFormData) => {
    if (!config || !activeTab || !editingGroupId) return;

    if (tileDialogMode === 'create') {
      // Create new tile
      const newTile: Tile = {
        id: `tile-${Date.now()}`,
        title: tileData.title,
        description: tileData.description || '',
        url: tileData.url,
        icon: tileData.icon || 'link',
        iconType: tileData.iconType,
        iconUrl: tileData.iconUrl,
        color: tileData.color,
        order: 0, // Will be set properly below
        openInNewTab: tileData.openInNewTab || false,
        isVisible: tileData.isVisible !== false
      };

      const newTabs = config.tabs?.map(tab => 
        tab.id === activeTab.id 
          ? { 
              ...tab, 
              groups: tab.groups.map(group => 
                group.id === editingGroupId 
                  ? { 
                      ...group, 
                      tiles: [...group.tiles, { ...newTile, order: group.tiles.length }]
                    }
                  : group
              )
            }
          : tab
      ) || [];

      const newConfig: DashboardConfig = {
        ...config,
        tabs: newTabs
      };
      await updateConfig(newConfig);
    } else if (editingTile) {
      // Edit existing tile
      const updatedTile: Tile = {
        ...editingTile,
        title: tileData.title,
        description: tileData.description || '',
        url: tileData.url,
        icon: tileData.icon || 'link',
        iconType: tileData.iconType,
        iconUrl: tileData.iconUrl,
        color: tileData.color,
        openInNewTab: tileData.openInNewTab || false,
        isVisible: tileData.isVisible !== false
      };

      const newTabs = config.tabs?.map(tab => 
        tab.id === activeTab.id 
          ? { 
              ...tab, 
              groups: tab.groups.map(group => 
                group.id === editingGroupId 
                  ? { 
                      ...group, 
                      tiles: group.tiles.map(tile => 
                        tile.id === updatedTile.id ? updatedTile : tile
                      )
                    }
                  : group
              )
            }
          : tab
      ) || [];

      const newConfig: DashboardConfig = {
        ...config,
        tabs: newTabs
      };
      await updateConfig(newConfig);
    }

    setTileDialogOpen(false);
  };

  // Title editing functions removed - using static display only

  // Global logo state
  const [globalSettings, setGlobalSettings] = useState<{ dashboardLogo?: { type: string; url?: string } } | null>(null);

  // Load global settings for dashboard logo
  React.useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        const response = await fetch('/api/users/global-settings');
        const data = await response.json();
        if (data.success && data.data) {
          setGlobalSettings(data.data.globalSettings);
        }
      } catch (error) {
        console.error('Failed to load global settings for preview:', error);
      }
    };
    
    loadGlobalSettings();
  }, []);

  // Get icon component for logo display - using global settings only
  const getLogoComponent = () => {
    // Use global dashboard logo if available
    if (globalSettings?.dashboardLogo?.type === 'image' && globalSettings.dashboardLogo.url) {
      return (
        <img
          src={globalSettings.dashboardLogo.url}
          alt="Dashboard Logo"
          style={{
            height: 32,
            marginRight: 8,
            opacity: 0.9,
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.9'}
        />
      );
    }
    
    // Default to material icon
    return (
      <ViewIcon sx={{ 
        fontSize: 32, 
        mr: 1, 
        opacity: 0.9,
        transition: 'opacity 0.2s ease'
      }} />
    );
  };

  if (!config) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info">Loading preview...</Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        border: '2px dashed #e0e0e0',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: 'background.default'
      }}
    >
      {/* Preview Header */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.50', borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewIcon />
            Dashboard Preview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Apply to Dashboard button for admin/superuser */}
            {user && (user.role === 'admin' || user.role === 'superuser') && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => setShowApplyConfirm(true)}
                disabled={isApplying || !config}
                startIcon={<SaveIcon />}
                sx={{ minWidth: 140 }}
              >
                Apply to Dashboard
              </Button>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={editMode}
                  onChange={toggleEditMode}
                  color="primary"
                />
              }
              label={editMode ? 'Edit Mode' : 'View Mode'}
            />
          </Box>
        </Box>
      </Paper>

      {/* Preview Header (simplified version) */}
      <Box
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Logo Section - uses global settings only */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mr: 2
          }}
        >
          {getLogoComponent()}
        </Box>

        {/* Title Section - static display only */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {config.layout?.headerTitle || 'Dashboard'}
          </Typography>
        </Box>
      </Box>

      {/* Tab Navigation (simplified) */}
      {config.tabs && config.tabs.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', alignItems: 'center' }}>
          <Tabs 
            value={config.tabs.findIndex(tab => tab.id === (config.activeTabId || config.tabs![0].id))}
            onChange={(e, newValue) => setActiveTab(config.tabs![newValue].id)}
            sx={{ flex: 1 }}
          >
            {config.tabs.map((tab) => (
              <MuiTab key={tab.id} label={tab.name} />
            ))}
          </Tabs>
          {editMode && (
            <IconButton
              onClick={handleTabCreate}
              size="small"
              sx={{ ml: 1 }}
            >
              <AddIcon />
            </IconButton>
          )}
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        p: 3,
        pb: editMode ? 10 : 3 // Extra padding when edit mode FABs are shown
      }}>
        {activeTab ? (
          <Box>
            {/* Get the current tab data from config to ensure latest groups after drag-drop */}
            {(() => {
              const currentTab = config?.tabs?.find(t => t.id === activeTab.id) || activeTab;
              return currentTab.groups && currentTab.groups.length > 0 ? (
                currentTab.groups.map((group: Group) => (
                <Box key={group.id} sx={{ mb: 4 }}>
                  {/* Group Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 8 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: group.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {group.name[0]}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
                      {group.name}
                    </Typography>
                    {editMode && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          onClick={() => handleTileCreate(group.id)}
                          size="small"
                          sx={{ color: 'primary.main' }}
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleGroupEdit(group)}
                          size="small"
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Group Tiles */}
                  <Box sx={{ px: 8 }}>
                    {group.tiles && group.tiles.length > 0 ? (
                      <Grid container spacing={3}>
                        {group.tiles.filter((tile: Tile) => tile.isVisible).map((tile: Tile, tileIndex: number) => (
                          <Grid item xs={12} sm={6} md={4} key={tile.id}>
                            <TileCard
                              tile={tile}
                              editMode={editMode}
                              index={tileIndex}
                              groupId={group.id}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        No tiles in this group
                      </Box>
                    )}
                  </Box>
                </Box>
              ))) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  No groups in this tab
                </Box>
              );
            })()}
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No active tab found
            </Typography>
          </Box>
        )}
      </Box>

      {/* Floating Action Buttons (Edit Mode) */}
      {editMode && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            zIndex: 1000,
          }}
        >
          {isDirty && (
            <Fab
              color="primary"
              aria-label="save changes"
              onClick={handleSave}
              size="small"
            >
              <SaveIcon />
            </Fab>
          )}
          
          <Fab
            color="secondary"
            aria-label="add new group"
            onClick={handleGroupCreate}
            size="small"
          >
            <AddIcon />
          </Fab>
        </Box>
      )}

      {/* Success Alert */}
      {showSaveSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2000
          }}
        >
          Preview updated!
        </Alert>
      )}

      {/* Apply Success Alert */}
      {applySuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2000
          }}
        >
          Configuration applied to your dashboard successfully!
        </Alert>
      )}

      {/* Apply Error Alert */}
      {applyError && (
        <Alert 
          severity="error" 
          sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2000
          }}
        >
          {applyError}
        </Alert>
      )}

      {/* Title update alerts removed with title editing functionality */}

      {/* Tab Edit Dialog */}
      <TabEditDialog
        open={tabDialogOpen}
        onClose={() => setTabDialogOpen(false)}
        onSubmit={handleTabSubmit}
        tab={editingTab}
        mode={tabDialogMode}
        isLoading={false}
      />

      {/* Group Edit Dialog */}
      <GroupEditDialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        group={editingGroup || undefined}
        mode={groupDialogMode}
        customOnSubmit={handleGroupSubmit}
      />

      {/* Tile Edit Dialog */}
      <TileEditDialog
        open={tileDialogOpen}
        onClose={() => setTileDialogOpen(false)}
        tile={editingTile || undefined}
        groupId={editingGroupId || undefined}
        mode={tileDialogMode}
        onSubmit={handleTileSubmit}
      />

      {/* Apply to Dashboard Confirmation Dialog */}
      <Dialog
        open={showApplyConfirm}
        onClose={() => setShowApplyConfirm(false)}
        maxWidth="sm"
      >
        <DialogTitle>Apply Configuration to Dashboard</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to apply this preview configuration to your actual dashboard? 
            This will replace your current dashboard layout and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowApplyConfirm(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setShowApplyConfirm(false);
              handleApplyToDashboard();
            }}
            variant="contained"
            color="secondary"
            disabled={isApplying}
            startIcon={isApplying ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {isApplying ? 'Applying...' : 'Apply to Dashboard'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box
        sx={{
          py: 1,
          px: 2,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Preview Mode - Changes saved to default template
          </Typography>
          <Box
            component="img"
            src={ThinkBeyondLogo}
            alt="ThinkBeyond"
            sx={{ height: 16, opacity: 0.7 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// Simple theme provider for preview
const PreviewThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { config } = usePreviewContext();
  
  const muiTheme = React.useMemo(() => {
    const defaultTheme: DashboardTheme = {
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      mode: 'light',
      backgroundColor: '#ffffff',
      textColor: '#000000',
      cardBackground: '#f5f5f5',
      borderRadius: 8,
      customColors: {
        accent1: '#ff5722',
        accent2: '#4caf50'
      }
    };
    
    const theme = config?.theme || defaultTheme;
    return createMuiTheme(theme);
  }, [config?.theme]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

// Main export component with all providers
const DashboardPreview: React.FC<DashboardPreviewProps> = ({
  config,
  onConfigChange,
  isEditable = true
}) => {
  return (
    <PreviewProvider config={config} onConfigChange={onConfigChange}>
      <PreviewThemeProvider>
        <PreviewContext.Consumer>
          {(dashboardContext) => (
            <DragDropProvider dashboardContext={dashboardContext || undefined}>
              <DashboardPreviewContent />
            </DragDropProvider>
          )}
        </PreviewContext.Consumer>
      </PreviewThemeProvider>
    </PreviewProvider>
  );
};

export default DashboardPreview;